import { createRoot } from "react-dom/client";

import "../posthog";

import { LandingI18nProvider } from "./i18n";
import LandingApp from "./LandingApp";

/**
 * Redirect returning / logged-in users straight to the app.
 *
 * Detection (checked before React mounts, so no FOUC):
 *  1. Supabase auth token in localStorage → user is logged in
 *
 * Shared-link passthrough: if the URL carries #room=, #json=, or ?id=
 * (collaboration / share links), forward them to /app so the app can handle them.
 *
 * Override: ?landing or ?landing=1 forces the landing page to show
 * regardless of auth state (useful for testing / previewing).
 */
function shouldRedirectToApp(): boolean {
  if (window.location.pathname.startsWith("/app")) {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  if (params.has("landing")) {
    return false;
  }

  const hash = window.location.hash;
  const search = window.location.search;

  const isSharedLink =
    hash.includes("room=") || hash.includes("json=") || search.includes("id=");
  const isOAuthCallback =
    params.has("code") ||
    hash.includes("access_token=") ||
    hash.includes("error_description=");

  if (isSharedLink || isOAuthCallback) {
    return true;
  }

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) {
        const val = localStorage.getItem(key);
        if (val) {
          const parsed = JSON.parse(val);
          if (parsed?.access_token) {
            return true;
          }
        }
      }
    }
  } catch {
    // localStorage unavailable or parse error — stay on landing
  }

  return false;
}

if (shouldRedirectToApp()) {
  window.location.replace(
    `/app${window.location.search}${window.location.hash}`,
  );
} else {
  const root = createRoot(document.getElementById("landing-root")!);
  root.render(
    <LandingI18nProvider>
      <LandingApp />
    </LandingI18nProvider>,
  );
}
